import {
  Goal,
  goalsArchivedSectionID,
  ID,
  LifeArea,
  lifeAreaOtherID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { BuildGoalListTreeOptions } from './build-deadline-goal-list-tree';

export type BuildLifeAreaGoalListTreeOptions = BuildGoalListTreeOptions & {
  otherLifeAreaName: string;
};

export const buildLifeAreaGoalListTree = (
  goals: Goal[],
  lifeAreas: LifeArea[],
  {
    hideArchived,
    hideCompleted,
    archivedSectionName,
    otherLifeAreaName,
  }: BuildLifeAreaGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const activeGoals: Goal[] = [];
  const archivedGoals: Goal[] = [];

  goals.forEach((goal) => {
    if (goal.archivedAt) {
      if (!hideArchived) {
        archivedGoals.push(goal);
      }
      return;
    }
    if (!hideCompleted || !goal.completedAt) {
      activeGoals.push(goal);
    }
  });

  const treeItems: Record<ID, TaskListTreeItem> = {};

  lifeAreas.forEach((lifeArea) => {
    treeItems[lifeArea.id] = {
      id: lifeArea.id,
      type: TaskListItemTypes.LifeArea,
      name: lifeArea.name,
      children: [],
      isFolder: true,
    };
  });

  treeItems[lifeAreaOtherID] = {
    id: lifeAreaOtherID,
    type: TaskListItemTypes.LifeArea,
    name: otherLifeAreaName,
    children: [],
    isFolder: true,
  };

  goals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: true,
    };
  });

  activeGoals.forEach((goal) => {
    const parentIds = goal.parentIds || [];
    const parentId = [...parentIds].reverse().find((id) => treeItems[id]);

    if (parentId) {
      treeItems[parentId].children?.push(goal.id);
      return;
    }

    const lifeAreaId = goal.lifeAreaId || lifeAreaOtherID;
    const targetId = treeItems[lifeAreaId] ? lifeAreaId : lifeAreaOtherID;
    treeItems[targetId].children?.push(goal.id);
  });

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: archivedSectionName,
      children: archivedGoals.map((goal) => goal.id),
      isFolder: true,
    };
  }

  const rootChildren = lifeAreas.reduce<ID[]>((acc, lifeArea) => {
    if (treeItems[lifeArea.id].children?.length) {
      acc.push(lifeArea.id);
    }
    return acc;
  }, []);

  if (treeItems[lifeAreaOtherID]?.children?.length) {
    rootChildren.push(lifeAreaOtherID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
